import { useState, useCallback, useEffect } from 'react';
import useLinkedStudiesApi from '../../api/requests/useLinkedStudiesApi';

/**
 * useLinkedStudiesSelection Hook
 *
 * Feature hook for managing linked studies selection in new request flow.
 * Fetches eligible studies and manages selection state.
 *
 * @param {number} workflowVersionId - The workflow version ID to fetch eligible studies for
 * @returns {Object} - { eligibleStudies, selectedStudyIds, setSelectedStudyIds, linkedStudiesEnabled, loading, error }
 */
const useLinkedStudiesSelection = (workflowVersionId) => {
  const { getEligibleLinkedStudies } = useLinkedStudiesApi();
  const [eligibleStudies, setEligibleStudies] = useState([]);
  const [selectedStudyIds, setSelectedStudyIds] = useState([]);
  const [linkedStudiesEnabled, setLinkedStudiesEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEligibleStudies = useCallback(async () => {
    if (!workflowVersionId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getEligibleLinkedStudies(workflowVersionId);
      setEligibleStudies(data.eligibleStudies || []);
      setLinkedStudiesEnabled(data.linkedStudiesEnabled || false);
    } catch (err) {
      console.error('Error fetching eligible linked studies:', err);
      setError(err.message || 'Failed to fetch eligible studies');
      setEligibleStudies([]);
    } finally {
      setLoading(false);
    }
  }, [workflowVersionId, getEligibleLinkedStudies]);

  useEffect(() => {
    fetchEligibleStudies();
  }, [fetchEligibleStudies]);

  return {
    eligibleStudies,
    selectedStudyIds,
    setSelectedStudyIds,
    linkedStudiesEnabled,
    loading,
    error,
    refetch: fetchEligibleStudies,
  };
};

export default useLinkedStudiesSelection;
